/**
 * Tenant persistence listener middleware.
 *
 * Closes the trap memorialized in feedback_tenant_switch_not_persisted.md:
 * `setActiveTenant` reducer writes Redux + localStorage only — never the
 * authoritative DB preferences. On F5, `TenantLoader` re-reads
 * `preferences.activeTenantId` (DB-authoritative per its own design comment),
 * sees null, and overrides localStorage with null — losing the user's choice.
 *
 * This listener centralizes the fix: every `setActiveTenant` dispatch from
 * any of the 5 caller sites (TenantPickerModal / TenantSwitcher value-change /
 * TenantSwitcher single-tenant jump / TenantLoader force-home auto-select /
 * TenantLoader single-tenant auto-select) automatically persists to DB.
 *
 * Idempotent guard: skips the API call when the new payload's tenantId
 * matches the current `preferences.activeTenantId` — eliminates the
 * redundant PUT that fires when TenantLoader reads from DB and
 * dispatches `setActiveTenant` with the same value it just read.
 *
 * Failure handling: API failure is logged-only (does not throw or revert
 * Redux state). The next page load's TenantLoader cycle picks up the
 * desync and recovers — at worst the user re-picks once.
 *
 * Implementation note: matches on action type string and calls the service
 * directly (NOT via thunk dispatch) to break the circular import chain
 * `tenantSlice → tenantThunks → tenantService → apiClient → store/index →
 * tenantPersistListener → tenantSlice`. The listener intentionally takes
 * NO imports from tenantSlice.
 *
 * Spec: pack-analysis-asn-browser-perf-fix follow-up (session-15).
 */

import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { TypedStartListening } from '@reduxjs/toolkit';
import { userPreferenceService } from '../../services/userPreferenceService';
import type { RootState, AppDispatch } from '../index';

const SET_ACTIVE_TENANT_ACTION_TYPE = 'tenant/setActiveTenant';

interface ActiveTenantPayload {
  tenantId: string;
  tenantCode: string;
  tenantName: string;
  configurationJson?: string | null;
}

interface SetActiveTenantAction {
  type: typeof SET_ACTIVE_TENANT_ACTION_TYPE;
  payload: ActiveTenantPayload | null;
}

export const tenantPersistListener = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;
const startAppListening = tenantPersistListener.startListening as AppStartListening;

startAppListening({
  predicate: (action) =>
    (action as { type?: string }).type === SET_ACTIVE_TENANT_ACTION_TYPE,
  effect: async (action, listenerApi) => {
    // Debounce / take-latest. A single tenant switch can dispatch
    // setActiveTenant more than once in quick succession — the picker
    // dispatches, then `/workspace/[tenantCode]/layout` re-resolves the URL
    // and dispatches again. Without coalescing, two `updateMultiplePreferences`
    // calls race on the user-preferences row's RowVersion and the loser throws
    // a DbUpdateConcurrencyException ("0 rows affected"). cancelActiveListeners()
    // aborts any earlier in-flight instance of this listener; the short delay
    // lets a rapidly-following dispatch cancel THIS instance before it issues
    // the API call — so only the final tenant choice is persisted.
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(200);

    const state = listenerApi.getState();

    // Bail when there's no authenticated user — happens during logout
    // bootstrap before authSlice clears, or in test stores without auth.
    const userIdRaw = state.auth.user?.id;
    if (!userIdRaw) return;
    const userId = Number(userIdRaw);
    if (!Number.isFinite(userId) || userId <= 0) return;

    const payload = (action as unknown as SetActiveTenantAction).payload;
    const newTenantId: number | null = payload
      ? Number(payload.tenantId)
      : null;

    // Idempotent guard: TenantLoader writes the value it JUST READ from
    // preferences — re-persisting the same value is a wasted PUT.
    const currentDbTenantId =
      state.userPreferences.preferences?.activeTenantId ?? null;
    if (currentDbTenantId === newTenantId) return;

    try {
      await userPreferenceService.updateMultiplePreferences(userId, {
        activeTenantId: newTenantId,
      });
    } catch (err) {
      // Log only — never break the Redux dispatch. TenantLoader catches up
      // on the next mount cycle (or the next time the user does anything
      // that triggers updateMultiplePreferences).
      // eslint-disable-next-line no-console
      console.warn(
        '[tenantPersistListener] Failed to persist activeTenantId to user preferences',
        err
      );
    }
  },
});
